/**
 * Phase 108.D — Live Reports panel.
 *
 * Generates 3 distinct, parent-facing report card comment options for a
 * single subject + Ontario achievement level. The LLM is grounded ONLY
 * in expectations the teacher has marked as TAUGHT (via the Lesson
 * Workspace, Library, or Math Engine exit ticket auto-hook).
 *
 * Lives as a separate tab inside the Report Card Builder.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Copy,
  Loader2,
  RefreshCw,
  Send,
  Sparkles,
  Wand2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { generateLiveReport } from "../lib/api";

const LEVEL_OPTIONS = [
  { value: "4", label: "Level 4 — Exceeding standard" },
  { value: "3", label: "Level 3 — Meeting standard (target)" },
  { value: "2", label: "Level 2 — Approaching standard" },
  { value: "1", label: "Level 1 — Below standard" },
];

const TONE_OPTIONS = [
  { value: "warmer", label: "Warmer" },
  { value: "more formal", label: "More formal" },
];
const LENGTH_OPTIONS = [
  { value: "more concise", label: "More concise" },
  { value: "more detailed", label: "More detailed" },
];
const JARGON_OPTIONS = [{ value: "reduce jargon", label: "Reduce jargon" }];

const FIELD_CLASS =
  "bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus:ring-2 focus:ring-[#96ded1] focus-visible:ring-[#96ded1] rounded-md h-11";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

// Pull subjects with ≥1 taught expectation so the picker only offers
// subjects the teacher can actually generate a grounded comment for.
const subjectsWithTaught = (subjects = []) =>
  subjects
    .map((s) => {
      const taughtCount = (s.expectations || []).filter((e) => e.taught).length;
      return { subject: s.subject, taughtCount };
    })
    .filter((s) => s.taughtCount > 0);

/* ------------------------------------------------------------------ */
/* Direction pills — multi-select toggle row                          */
/* ------------------------------------------------------------------ */

const PillRow = ({ label, options, value, onChange, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-[#4a6984] font-bold text-[10px] uppercase tracking-[0.18em]">
      {label}
    </Label>
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(active ? null : opt.value)}
            data-testid={`${testId}-${opt.value.replace(/\s+/g, "-")}`}
            aria-pressed={active}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
              active
                ? "bg-[#96ded1] border-[#1c9e7e] text-[#1c2d3d] shadow-sm"
                : "bg-white border-[#4a6984]/25 text-[#4a6984]/80 hover:border-[#4a6984]/45 hover:text-[#4a6984]"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Single comment card                                                 */
/* ------------------------------------------------------------------ */

const CommentCard = ({ index, comment, onCopy, onRefine, onSend, refining, sendLabel }) => {
  const labelLetter = ["A", "B", "C"][index] || String(index + 1);
  const angle = [
    "Curriculum knowledge & skills",
    "Learning process & strategies",
    "Growth journey & next steps",
  ][index] || "Option";
  return (
    <div
      className="ae-card p-4 border border-[#4a6984]/15 bg-white space-y-3"
      data-testid={`live-report-comment-${index}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className="ae-pill !py-0.5 !px-2 !text-[10px] !font-extrabold bg-[#96ded1]/45 border border-[#96ded1] text-[#1c2d3d] uppercase tracking-[0.12em]"
          >
            Option {labelLetter}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#4a6984]/65">
            {angle}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onCopy(comment)}
            data-testid={`live-report-copy-${index}`}
            className="h-8 px-2 text-[#4a6984] hover:bg-[#f4f5f7]"
            title="Copy to clipboard"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRefine(index, comment)}
            disabled={refining}
            data-testid={`live-report-refine-${index}`}
            className="h-8 px-2 text-[#4a6984] hover:bg-[#f4f5f7]"
            title="Regenerate this option with a nudge"
          >
            {refining ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>
      <p className="text-sm text-[#1c2d3d] leading-relaxed whitespace-pre-wrap">
        {comment}
      </p>
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-[#4a6984]/10">
        <p className="text-[10px] text-[#4a6984]/60 font-medium">
          {comment.length} characters
        </p>
        {/* Phase 108.F — Send this comment to the matching subject tab.
            Hidden when the target subject isn't a builder tab (e.g. some
            edge buckets); the parent passes sendLabel=null in that case. */}
        {sendLabel ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onSend(index, comment)}
            data-testid={`live-report-send-${index}`}
            className="h-8 px-3 text-[11px] font-extrabold uppercase tracking-[0.1em] border-[#1c9e7e]/45 text-[#1c9e7e] hover:bg-[#1c9e7e]/10 hover:border-[#1c9e7e]"
            title={`Append this comment to your ${sendLabel} draft and switch to that tab`}
          >
            <Send className="h-3 w-3 mr-1.5" />
            Send to {sendLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Sources panel                                                      */
/* ------------------------------------------------------------------ */

const SourcesPanel = ({ sources = [] }) => {
  if (!sources.length) return null;
  return (
    <div
      className="ae-card p-4 border border-[#4a6984]/15 bg-[#f4f5f7]/60 space-y-2"
      data-testid="live-report-sources"
    >
      <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984]/80">
        Grounded in {sources.length} taught expectation{sources.length === 1 ? "" : "s"}
      </p>
      <ul className="space-y-1.5">
        {sources.map((s) => (
          <li
            key={s.code}
            className="text-xs text-[#4a6984] leading-relaxed"
            data-testid={`live-report-source-${s.code}`}
          >
            <span className="font-extrabold mr-1">{s.code}</span>
            {s.description}
            {s.activity_names?.length ? (
              <span className="block text-[10px] text-[#4a6984]/70 mt-0.5 pl-2 border-l-2 border-[#96ded1]">
                via {s.activity_names.map((n) => `"${n}"`).join(", ")}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main panel                                                          */
/* ------------------------------------------------------------------ */

const LiveReportsPanel = ({ subjects = [], grade = "", reportType = "", onSendToSubject, sendableSubjects = null }) => {
  const availableSubjects = useMemo(() => subjectsWithTaught(subjects), [subjects]);

  const [subject, setSubject] = useState("");
  const [level, setLevel] = useState("3");
  const [tone, setTone] = useState(null);
  const [length, setLength] = useState(null);
  const [jargon, setJargon] = useState(null);
  const [nudge, setNudge] = useState("");
  const [loading, setLoading] = useState(false);
  const [refineIndex, setRefineIndex] = useState(null);
  const [result, setResult] = useState(null); // { comments, sources, subject, level, notice }

  const directionPayload = useMemo(() => {
    const d = {};
    if (tone) d.tone = tone;
    if (length) d.length = length;
    if (jargon) d.jargon = jargon;
    return Object.keys(d).length ? d : null;
  }, [tone, length, jargon]);

  const handleGenerate = async () => {
    if (!subject) {
      toast.error("Pick a subject first.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await generateLiveReport({
        subject,
        level: Number(level),
        studentGrade: grade,
        direction: directionPayload,
        nudge,
        reportType,
      });
      setResult(res);
      if (res?.notice) {
        toast(res.notice);
      } else if ((res?.comments || []).length) {
        toast.success(`Generated ${res.comments.length} comment options.`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      toast.error("Couldn't generate live comments", { description: String(detail) });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Comment copied to clipboard.");
    } catch {
      toast.error("Copy failed — please select and copy manually.");
    }
  };

  // Phase 108.F — handoff to the subject tab's draft textarea.
  const handleSend = (_index, text) => {
    if (!subject || !onSendToSubject) return;
    onSendToSubject(subject, text);
  };

  // The send button only shows up when the parent says the subject
  // maps to a builder tab (i.e. it's not a Live-Reports-only bucket).
  const sendLabel = useMemo(() => {
    if (!subject || !onSendToSubject) return null;
    if (Array.isArray(sendableSubjects) && !sendableSubjects.includes(subject)) {
      return null;
    }
    // Compact tab label — strip parenthetical clarifiers like "Core French".
    const short = subject.split("/")[0].trim();
    return short.length > 18 ? `${short.slice(0, 16)}…` : short;
  }, [subject, onSendToSubject, sendableSubjects]);

  // Per-card refine: rerun the model with the prior comment + current nudge
  // and direction pills, then splice the refined copy back into position so
  // the teacher keeps the other two options intact.
  const handleRefine = async (index, priorComment) => {
    setRefineIndex(index);
    try {
      const res = await generateLiveReport({
        subject,
        level: Number(level),
        studentGrade: grade,
        direction: directionPayload,
        nudge,
        priorComment,
        reportType,
      });
      const next = [...(result?.comments || [])];
      if (res?.comments?.length) {
        next[index] = res.comments[0];
        setResult({ ...result, comments: next });
        toast.success(`Option ${["A", "B", "C"][index] || index + 1} refined.`);
      } else if (res?.notice) {
        toast(res.notice);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      toast.error("Refine failed", { description: String(detail) });
    } finally {
      setRefineIndex(null);
    }
  };

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */

  // No subjects at all (teacher hasn't marked anything Taught anywhere).
  if (!availableSubjects.length) {
    return (
      <div
        className="ae-card p-8 border border-dashed border-[#4a6984]/25 bg-white text-center"
        data-testid="live-report-empty-overall"
      >
        <Sparkles className="h-7 w-7 text-[#96ded1] mx-auto mb-3" />
        <p className="text-[#4a6984] font-extrabold text-base">
          Nothing marked as Taught yet
        </p>
        <p className="text-sm text-[#4a6984]/75 mt-2 max-w-md mx-auto leading-relaxed">
          Live Reports draw straight from lessons you&apos;ve confirmed teaching.
          Open the Lesson Workspace or Library and tap{" "}
          <span className="ae-pill !py-0 !px-1.5 !text-[9px] !font-extrabold bg-[#1c9e7e] text-white border border-[#1c9e7e] uppercase tracking-[0.1em]">
            <Sparkles className="h-2 w-2 inline -mt-0.5" /> Mark as Taught
          </span>{" "}
          on any lesson — that lesson&apos;s expectations will become eligible here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="live-report-panel">
      {/* Intro card */}
      <div className="ae-card p-5 border border-[#96ded1]/45 bg-gradient-to-br from-[#96ded1]/15 to-white">
        <p className="font-extrabold uppercase text-[10px] tracking-[0.22em] text-[#1c9e7e] inline-flex items-center gap-2">
          <Wand2 className="h-3.5 w-3.5" /> Live Reports · Phase 108.D
        </p>
        <h3 className="mt-1 font-black text-xl text-[#4a6984] tracking-tight">
          Generate parent-facing comments grounded only in what&apos;s been taught.
        </h3>
        <p className="mt-2 text-sm text-[#4a6984]/85 leading-relaxed max-w-3xl">
          Pick a subject and Ontario achievement level. The model produces three
          distinct comment angles — knowledge, process, and growth — anchored
          strictly in expectations you&apos;ve marked{" "}
          <span className="ae-pill !py-0 !px-1.5 !text-[9px] !font-extrabold bg-[#1c9e7e] text-white border border-[#1c9e7e] uppercase tracking-[0.1em]">
            <Sparkles className="h-2 w-2 inline -mt-0.5" /> Taught
          </span>
          . Nothing the student wasn&apos;t taught will be invented.
        </p>
      </div>

      {/* Controls */}
      <div className="ae-card p-5 border border-[#4a6984]/15 bg-white space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
              Subject
            </Label>
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger data-testid="live-report-subject" className={FIELD_CLASS}>
                <SelectValue placeholder="Pick a subject with taught lessons" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
                {availableSubjects.map((s) => (
                  <SelectItem
                    key={s.subject}
                    value={s.subject}
                    data-testid={`live-report-subject-${s.subject.replace(/\s+/g, "-")}`}
                    className="text-[#4a6984] focus:bg-[#f4f5f7]"
                  >
                    {s.subject}{" "}
                    <span className="text-[10px] text-[#1c9e7e] font-extrabold ml-1">
                      · {s.taughtCount} taught
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
              Achievement level
            </Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger data-testid="live-report-level" className={FIELD_CLASS}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
                {LEVEL_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                    data-testid={`live-report-level-${opt.value}`}
                    className="text-[#4a6984] focus:bg-[#f4f5f7]"
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <PillRow
            label="Tone"
            options={TONE_OPTIONS}
            value={tone}
            onChange={setTone}
            testId="live-report-tone"
          />
          <PillRow
            label="Length"
            options={LENGTH_OPTIONS}
            value={length}
            onChange={setLength}
            testId="live-report-length"
          />
          <PillRow
            label="Plain language"
            options={JARGON_OPTIONS}
            value={jargon}
            onChange={setJargon}
            testId="live-report-jargon"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
            Teacher nudge <span className="text-[#4a6984]/60 font-medium normal-case">(optional)</span>
          </Label>
          <Textarea
            value={nudge}
            onChange={(e) => setNudge(e.target.value)}
            placeholder='e.g. "Mention the small-group work [Name] led in our Forces unit."'
            rows={2}
            data-testid="live-report-nudge"
            className="bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus:ring-2 focus:ring-[#96ded1] rounded-md"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button
            type="button"
            onClick={handleGenerate}
            disabled={loading || !subject}
            data-testid="live-report-generate"
            className="h-11 px-6 bg-[#1c9e7e] hover:bg-[#177a61] text-white font-bold rounded-md shadow-sm disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating 3 options…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate live comments
              </>
            )}
          </Button>
          {result?.comments?.length ? (
            <span className="text-xs text-[#4a6984]/70 font-medium" data-testid="live-report-status">
              Showing {result.comments.length} option{result.comments.length === 1 ? "" : "s"} for {result.subject} · Level {result.level}
            </span>
          ) : null}
        </div>
      </div>

      {/* Result */}
      {result?.notice && !result.comments?.length ? (
        <div
          className="ae-card p-5 border border-dashed border-[#4a6984]/25 bg-white text-center"
          data-testid="live-report-notice"
        >
          <p className="text-sm text-[#4a6984] font-medium leading-relaxed max-w-xl mx-auto">
            {result.notice}
          </p>
        </div>
      ) : null}

      {result?.comments?.length ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="live-report-results">
          {result.comments.map((c, i) => (
            <CommentCard
              key={i}
              index={i}
              comment={c}
              onCopy={handleCopy}
              onRefine={handleRefine}
              onSend={handleSend}
              sendLabel={sendLabel}
              refining={refineIndex === i}
            />
          ))}
        </div>
      ) : null}

      {result?.sources?.length ? <SourcesPanel sources={result.sources} /> : null}
    </div>
  );
};

export default LiveReportsPanel;
